import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

const scrollAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

const BannerContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  padding: '32px 24px',
  backgroundColor: '#1e293b', // slate-800
  borderTop: '2px solid transparent',
  borderBottom: '2px solid transparent',
  borderLeft: 'none',
  borderRight: 'none',
  borderImage: 'linear-gradient(90deg, #22d3ee, #a5b4fc) 1',
  borderImageSlice: '1 0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  opacity: 0,
  transform: 'translateY(20px)',
  transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
  '&.is-visible': {
    opacity: 1,
    transform: 'translateY(0)'
  },
  [theme.breakpoints.down('md')]: {
    padding: '24px 20px'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '20px 16px',
    flexDirection: 'column',
    gap: '16px'
  }
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '60px',
  width: '100%',
  [theme.breakpoints.down('md')]: {
    gap: '40px'
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: '16px'
  }
}));

const BannerText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '20px',
  color: '#ffffff',
  textAlign: 'center',
  letterSpacing: '0',
  lineHeight: '1.2',
  whiteSpace: 'nowrap',
  background: 'linear-gradient(90deg, #22d3ee, #a5b4fc)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  [theme.breakpoints.down('md')]: {
    fontSize: '18px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    whiteSpace: 'normal'
  }
}));

const LogoScrollContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '60px',
  animation: `${scrollAnimation} 20s linear infinite`,
  '&:hover': {
    animationPlayState: 'paused'
  },
  [theme.breakpoints.down('sm')]: {
    gap: '40px'
  }
}));

const Logo = styled('img')(({ theme }) => ({
  height: '32px',
  width: 'auto',
  filter: 'brightness(0) invert(1)', // Makes logos white
  opacity: 0.9,
  transition: 'opacity 0.3s ease, transform 0.3s ease',
  '&:hover': {
    opacity: 1,
    transform: 'scale(1.1)'
  },
  [theme.breakpoints.down('sm')]: {
    height: '24px'
  }
}));

const IntegrationBanner = () => {
  const bannerRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    if (bannerRef.current) {
      observer.observe(bannerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <BannerContainer ref={bannerRef} className="animate-on-scroll">
      <ContentWrapper>
        <BannerText>
          Integration With Directories
        </BannerText>
        <Box sx={{
          flex: 1,
          overflow: 'hidden',
          position: 'relative',
          height: '32px',
          maxWidth: { xs: '300px', sm: '500px', md: '800px' }
        }}>
          <Box
            component="img"
            src="https://c.animaapp.com/mgbvruh0ta8bZ7/img/frame-1000005331.svg"
            alt="Directory Logos"
            sx={{
              position: 'absolute',
              height: '100%',
              width: 'auto',
              left: 0,
              top: 0,
              animation: `${scrollAnimation} 20s linear infinite`
            }}
          />
          <Box
            component="img"
            src="https://c.animaapp.com/mgbvruh0ta8bZ7/img/frame-1000005331.svg"
            alt="Directory Logos"
            sx={{
              position: 'absolute',
              height: '100%',
              width: 'auto',
              left: 'calc(100% + 60px)',
              top: 0,
              animation: `${scrollAnimation} 20s linear infinite`
            }}
          />
        </Box>
      </ContentWrapper>
    </BannerContainer>
  );
};

export default IntegrationBanner;
